import React, { useEffect, useState } from 'react';
import { PencilLine } from 'lucide-react';
import { cn } from '../../../lib/utils';

const shellClass = (isEditing, compact) =>
  cn(
    'w-full rounded-lg border text-left transition-colors',
    isEditing
      ? 'border-emerald-400/70 bg-white dark:border-emerald-700/80 dark:bg-gray-900/60'
      : 'border-dashed border-gray-200 bg-white/80 dark:border-gray-700 dark:bg-gray-900/50 hover:border-emerald-300 hover:bg-emerald-50/50 dark:hover:border-emerald-800 dark:hover:bg-emerald-950/30',
    compact ? 'px-2.5 py-2' : 'px-3 py-2.5'
  );

const textClass = (compact) =>
  cn(
    'w-full bg-transparent font-medium leading-relaxed text-gray-700 outline-none dark:text-gray-200',
    compact ? 'text-xs' : 'text-sm'
  );

const EditableDescriptionField = ({
  value,
  onSave,
  multiline = false,
  compact = false,
  className,
  emptyLabel = 'No description',
  placeholder,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');

  useEffect(() => {
    if (!isEditing) setDraft(value || '');
  }, [value, isEditing]);

  const isEmpty = !value || !String(value).trim();

  const cancel = () => {
    setDraft(value || '');
    setIsEditing(false);
  };

  const save = () => {
    onSave?.(draft);
    setIsEditing(false);
  };

  if (isEditing) {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancel();
        return;
      }
      if (e.key === 'Enter' && !multiline) {
        e.preventDefault();
        save();
        return;
      }
      if (e.key === 'Enter' && multiline && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        save();
      }
    };

    return (
      <div className={cn(shellClass(true, compact), className)}>
        {multiline ? (
          <textarea
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={Math.max(2, String(draft).split('\n').length)}
            className={cn(textClass(compact), 'resize-y min-h-[2.5rem]')}
            placeholder={placeholder || emptyLabel}
          />
        ) : (
          <input
            autoFocus
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                save();
                return;
              }
              handleKeyDown(e);
            }}
            className={textClass(compact)}
            placeholder={placeholder || emptyLabel}
          />
        )}
        <div className="mt-2 flex items-center justify-end gap-3 border-t border-gray-100 pt-2 dark:border-gray-800">
          <button
            type="button"
            onClick={cancel}
            className="text-[9px] font-bold uppercase tracking-wider text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-300"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={draft === (value || '')}
            className="text-[9px] font-bold uppercase tracking-wider text-emerald-600 transition-colors hover:text-emerald-700 disabled:opacity-40 dark:text-emerald-400"
          >
            Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setIsEditing(true)}
      className={cn(shellClass(false, compact), 'group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40', className)}
    >
      <span className="flex items-start gap-2">
        <PencilLine
          size={compact ? 12 : 13}
          className="mt-0.5 shrink-0 text-gray-300 transition-colors group-hover:text-emerald-500 dark:text-gray-600 dark:group-hover:text-emerald-400"
          aria-hidden
        />
        <span
          className={cn(
            'min-w-0 flex-1 text-left font-medium leading-relaxed whitespace-pre-wrap',
            compact ? 'text-xs' : 'text-sm',
            isEmpty ? 'italic text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-200'
          )}
        >
          {isEmpty ? emptyLabel : value}
        </span>
      </span>
    </button>
  );
};

export default EditableDescriptionField;
